// Screen 16 — Reporting keys (admin, multi-tenant).
//
// All issued reporting keys across tenants: a masked prefix, the owning client
// (resolved from /admin/clients), a label, the created timestamp, and an
// Enabled|Disabled status.
//   • Issue — pick a client + label; the backend returns the raw key ONCE
//     (stored only as a hash). The raw value is shown once, copyable, then gone.
//   • Revoke — disables a key immediately (fast revocation; the key surface goes
//     dark on the client side).
//
// REAL SHAPES (admin Go handlers — source of truth):
//   GET  /admin/reporting-keys -> { reporting_keys: { id, client_id, prefix, name,
//                                                      enabled, created_at }[] }
//   POST /admin/clients/{id}/reporting-keys            (issue — per-client)
//   POST /admin/clients/{id}/reporting-keys/{kid}/revoke (revoke — per-client)
// There is no `client` name or `last_used` on the row, so the client name is
// resolved from /admin/clients and last-used is omitted. Issue/revoke are the
// per-client routes (the picker is populated from /admin/clients).
//
// SECURITY: the raw reporting key is shown exactly once on issue and is never
// re-fetchable — the backend keeps only a hash. This portal never displays a
// raw key for an existing row (only the non-secret prefix).

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckIcon, CopyIcon } from "lucide-react";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  Modal,
  StatusPill,
  type Column,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NativeSelect } from "./controls";

// The cross-tenant global reporting-key row, exactly as the backend serializes
// it. No `client` name and no `last_used` — the name is resolved client-side.
interface ReportingKey {
  id: string;
  client_id: string;
  prefix: string;
  name: string;
  enabled: boolean;
  created_at: number;
}

// The create response — the ONLY shape carrying the raw key, returned once.
interface IssuedKey {
  id: string;
  prefix: string;
  reporting_key: string;
  enabled: boolean;
}

interface ClientRef {
  id: string;
  name: string;
}

function fmtTs(ts: number): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString();
}

export function ReportingKeysPage() {
  const [keys, setKeys] = useState<ReportingKey[]>([]);
  const [clients, setClients] = useState<ClientRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  function clientName(id: string): string {
    return clients.find((c) => c.id === id)?.name ?? id ?? "—";
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ reporting_keys: ReportingKey[] }>(
        "/admin/reporting-keys",
      );
      setKeys(res.reporting_keys ?? []);
      const c = await api
        .get<{ clients: ClientRef[] }>("/admin/clients")
        .catch(() => null);
      if (c) setClients(c.clients ?? []);
    } catch (err) {
      setError(errMessage(err));
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(key: ReportingKey) {
    setBusy(key.id);
    try {
      // Revoke is the per-client route: /admin/clients/{client_id}/reporting-keys/{id}/revoke.
      await api.post(
        `/admin/clients/${key.client_id}/reporting-keys/${key.id}/revoke`,
      );
      toast.success(`${clientName(key.client_id)}: reporting key revoked`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  const columns: Column<ReportingKey>[] = [
    {
      key: "key",
      header: "Key",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.prefix}
          <span className="text-nx-text-3">…</span>
        </span>
      ),
    },
    {
      key: "client",
      header: "Client",
      cell: (r) => (
        <span className="font-medium text-nx-text">
          {clientName(r.client_id)}
        </span>
      ),
    },
    {
      key: "label",
      header: "Label",
      cell: (r) =>
        r.name ? (
          <span className="text-nx-text-2">{r.name}</span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "created",
      header: "Created",
      cell: (r) => (
        <span className="text-xs text-nx-text-3">{fmtTs(r.created_at)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) =>
        r.enabled ? (
          <StatusPill tone="live">Enabled</StatusPill>
        ) : (
          <StatusPill tone="na">Disabled</StatusPill>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) =>
        r.enabled ? (
          <Button
            variant="ghost"
            size="xs"
            disabled={busy === r.id}
            onClick={() => revoke(r)}
            className="text-nx-debit hover:text-nx-debit"
          >
            Revoke
          </Button>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-semibold">Reporting keys</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Per-client reporting keys (e.g. Google Sheets pulls). The raw key is
            shown once on issue and stored only as a hash; revoke disables it
            immediately.
          </p>
        </div>
        <Button onClick={() => setIssueOpen(true)}>Issue key</Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load reporting keys</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={keys}
          rowKey={(r) => r.id}
          empty="No reporting keys issued."
        />
      )}

      <IssueKeyModal
        open={issueOpen}
        clients={clients}
        onOpenChange={(o) => {
          setIssueOpen(o);
          if (!o) setIssued(null);
        }}
        issued={issued}
        onIssued={async (k) => {
          setIssued(k);
          await load();
        }}
      />
    </AdminShell>
  );
}

function IssueKeyModal({
  open,
  clients,
  onOpenChange,
  issued,
  onIssued,
}: {
  open: boolean;
  clients: ClientRef[];
  onOpenChange: (open: boolean) => void;
  issued: IssuedKey | null;
  onIssued: (k: IssuedKey) => void | Promise<void>;
}) {
  const [clientId, setClientId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function issue() {
    if (!clientId) return;
    setBusy(true);
    try {
      // Issue is the per-client route; the body carries only the optional name.
      const k = await api.post<IssuedKey>(
        `/admin/clients/${clientId}/reporting-keys`,
        label ? { name: label } : {},
      );
      toast.success("Reporting key issued — copy it now, it won't be shown again");
      await onIssued(k);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!issued) return;
    try {
      await navigator.clipboard?.writeText(issued.reporting_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  function close() {
    setClientId("");
    setLabel("");
    setCopied(false);
    onOpenChange(false);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
        else onOpenChange(o);
      }}
      title="Issue reporting key"
      description={
        issued
          ? "Copy this key now — it is shown once and stored only as a hash."
          : "Pick a client and label. The raw key is shown once on issue."
      }
      footer={
        issued ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button disabled={!clientId || busy} onClick={issue}>
              Issue key
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <div className="grid gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-nx-border bg-nx-bg-2 px-3 py-2">
            <code className="flex-1 font-mono text-sm break-all text-nx-text tabular-nums">
              {issued.reporting_key}
            </code>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={copy}
              aria-label={copied ? "Copied" : "Copy key"}
            >
              {copied ? (
                <CheckIcon className="text-nx-credit" />
              ) : (
                <CopyIcon />
              )}
            </Button>
          </div>
          <Alert className="border-nx-warn/30 bg-nx-warn-soft">
            <AlertTitle className="text-nx-warn">Store it now</AlertTitle>
            <AlertDescription className="text-nx-text-2">
              This is the only time the raw key is shown. We keep a hash only —
              we cannot recover it later.
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <div className="grid gap-4">
          <div>
            <label
              htmlFor="rk-client"
              className="mb-1 block text-sm font-medium text-nx-text"
            >
              Client
            </label>
            <NativeSelect
              id="rk-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label
              htmlFor="rk-label"
              className="mb-1 block text-sm font-medium text-nx-text"
            >
              Label
            </label>
            <Input
              id="rk-label"
              placeholder="e.g. Gmart spend sheet"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
